import {
  type ColumnDef,
  createSortedRowModel,
  metaHelper,
  type RowData,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
} from "@tanstack/react-table";

/**
 * Column metadata owned by ATD's DataTable (alignment, sticky, numeric).
 * Typed via the v9 `columnMeta` feature slot — no global `declare module`.
 */
export interface DataTableColumnMeta {
  /** Column alignment. Numeric columns should be `"end"`. */
  align?: "start" | "center" | "end";
  /** Renders the cell with tabular figures so digits do not jitter. */
  numeric?: boolean;
  /** Any CSS width, e.g. `"12rem"`. */
  width?: string;
  /** Pins this column to the start or end edge of its scroll container. */
  sticky?: "start" | "end";
  /** Offset from the configured sticky edge, e.g. `"3rem"`. */
  stickyOffset?: string;
  /** Extra class names on body cells for this column. */
  className?: string;
  /** Extra class names on the header cell for this column. */
  headerClassName?: string;
  /**
   * When `onColumnReorder` is set, this column can be dragged. Defaults to
   * true. Pin synthetic columns (selection, trailing actions) with `false`.
   */
  reorderable?: boolean;
}

/**
 * Shared feature set for every ATD DataTable: client sorting + column meta.
 * Keep this module-level so `typeof dataTableFeatures` stays stable for types.
 */
export const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  // Auto sortFn picks among these by sampling cell values.
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    text: sortFn_text,
    datetime: sortFn_datetime,
    basic: sortFn_basic,
  },
  columnMeta: metaHelper<DataTableColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;

/**
 * TanStack's ColumnDef is invariant in its value type, so a list of columns
 * over different value types has no single sound annotation. `any` is
 * TanStack's own documented signature for a heterogeneous column array; the
 * row type TData stays fully checked, which is the part that matters.
 */
export type DataTableColumns<TData extends RowData> = ColumnDef<
  DataTableFeatures,
  TData,
  // biome-ignore lint/suspicious/noExplicitAny: see above — TanStack's documented column-array signature
  any
>[];
